
import React from 'react';
import Layout from '@/components/Layout';
import SearchBar from '@/components/SearchBar';
import CategoryGrid from '@/components/CategoryGrid';
import { BadgeSections } from '@/components/BadgeSections';
import { FadeIn } from '@/components/Animations';
import { getIconComponent } from '@/utils/iconUtils';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { useCommunity } from '@/contexts/CommunityContext';
import { useCategoryGroups } from '@/hooks/useCategoryGroups';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { useTranslation } from 'react-i18next';
import { useTranslatedDocumentTitle } from '@/hooks/useDocumentTitle';
import { motion } from 'framer-motion';
import { parseStyledTranslation } from '@/utils/translationUtils';
import { useCategoryBrowsingParameter, useBadgeConfiguration, useBusinessParameters } from '@/hooks/useParameters';
import { useBadges } from '@/hooks/useBadges';
import { prepareServiceForCard } from '@communitly/shared';
import { AllServicesSection } from '@/components/AllServicesSection';

const Index = () => {
  const { currentCommunity } = useCommunity();
  const { data: categoryGroupsData, isLoading: categoryGroupsLoading } = useCategoryGroups();
  const { user } = useAuth();
  const { isBusinessOwner, isLoading: isBusinessLoading } = useBusiness();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { categoryBrowsingEnabled } = useCategoryBrowsingParameter();
  const { defaultBannerUrl } = useBusinessParameters();
  
  // Badge system hooks
  const { badgeSections, isLoading: badgeConfigLoading } = useBadgeConfiguration();
  const { data: badges, isLoading: badgesLoading } = useBadges(currentCommunity?.id || '');
  
  // Set page title using the hook with proper error handling
  useTranslatedDocumentTitle('home.title', { community: currentCommunity?.name || '' });
  
  // Icon mapping for badges using centralized utility
  const getBadgeIcon = (iconName: string) => {
    return getIconComponent(iconName);
  };

  // Get badge display name using translation system
  const getBadgeDisplayName = (badgeSlug: string) => {
    const badge = badges?.find(b => b.slug === badgeSlug);
    return badge?.translatedName || badge?.name || badgeSlug;
  };

  const prepareServiceForCardWrapper = (service: unknown) => {
    return prepareServiceForCard(
      service,
      defaultBannerUrl,
      {
        unknownLocation: t('home.services.unknown.location'),
        unnamed: t('home.services.unnamed'),
        uncategorized: t('home.services.uncategorized'),
      }
    );
  };

  return (
    <Layout>
      {/* Hero Section with Integrated Search and Category Grid */}
      <section className="relative pt-4 pb-4 lg:pt-24 lg:pb-12 overflow-hidden">
        {/* Enhanced brand-colored gradient background using CSS variables */}
        <div 
          className="absolute inset-0 z-0 hidden md:block"
          style={{
            background: `linear-gradient(to bottom, 
              hsl(var(--primary) / 0.05) 0%, 
              hsl(var(--primary) / 0.03) 70%, 
              hsl(var(--primary) / 0.01) 100%)`
          }}
        ></div>
        
        {/* Subtle decorative elements using community primary color */}
        <div className="absolute right-0 top-0 w-full h-full overflow-hidden z-0 hidden md:block">
          <svg width="100%" height="100%" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" opacity="0.04">
            <defs>
              <linearGradient id="g1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={currentCommunity?.primary_color || '#f97316'} stopOpacity="0.5" />
                <stop offset="100%" stopColor={currentCommunity?.primary_color || '#f97316'} stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <path d="M0,0 C300,100 400,300 500,300 C600,300 700,100 1000,0 L1000,1000 L0,1000 Z" fill="url(#g1)" />
          </svg>
        </div>
        
        {/* Subtle circular decorative elements using community colors */}
        <div 
          className="absolute top-1/4 right-1/4 w-32 h-32 rounded-full blur-xl hidden md:block"
          style={{
            backgroundColor: `hsl(var(--primary) / 0.08)`
          }}
        ></div>
        <div 
          className="absolute bottom-1/3 left-1/6 w-24 h-24 rounded-full blur-lg hidden md:block"
          style={{
            backgroundColor: `hsl(var(--primary) / 0.06)`
          }}
        ></div>
        
        <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <FadeIn direction="up" delay={0.1} className="mb-6 hidden lg:block">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center leading-snug">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
                  {parseStyledTranslation(t('home.hero.headline'), { 
                    primaryColor: currentCommunity?.primary_color || '#6b7280' 
                  })}
                </span>
              </h1>
            </FadeIn>
            
            <FadeIn direction="up" delay={0.2} className="mb-6 hidden lg:block">
              <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                {t('home.hero.subtitle')}
              </p>
            </FadeIn>

            {/* Integrated Search Component - only shown on desktop */}
            <FadeIn direction="up" delay={0.3} className="max-w-3xl mx-auto mb-8 hidden lg:block">
              <SearchBar 
                className="w-full" 
                placeholder={t('home.search.placeholder')}
              />
            </FadeIn>
          </div>
          
          {/* Category Grid - Enhanced spacing and positioning */}
          {categoryBrowsingEnabled && (
            <FadeIn direction="up" delay={0.4} className="max-w-5xl mx-auto">
              <CategoryGrid 
                categoryGroups={categoryGroupsData?.categoryGroups || []} 
                isLoading={categoryGroupsLoading}
              />
            </FadeIn>
          )}
        </div>
        
        {/* Soft gradient divider to mark end of hero */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-b from-transparent to-white/50"></div>
      </section>

      {/* Dynamic Badge Sections */}
      <BadgeSections 
        badgeConfig={badgeSections}
        badges={badges}
        currentCommunity={currentCommunity}
        prepareServiceForCard={prepareServiceForCardWrapper}
        getBadgeIcon={getBadgeIcon}
        getBadgeDisplayName={getBadgeDisplayName}
        loading={badgeConfigLoading || badgesLoading}
      />

      {/* All Services Section with Infinite Scroll */}
      <AllServicesSection
        prepareServiceForCard={prepareServiceForCardWrapper}
      />

      {/* CTA Section */}
      <section 
        className="py-16 lg:py-20 px-4 md:px-6"
        style={{
          background: `linear-gradient(to bottom, 
            hsl(var(--primary) / 0.04) 0%, 
            white 100%)`
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden shadow-xl relative">
            {/* Background with gradient using community primary color */}
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, 
                  hsl(var(--primary)) 0%, 
                  hsl(var(--primary) / 0.8) 100%)`
              }}
            ></div>
            
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/3 translate-x-1/3"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3"></div>
            
            <div className="relative z-10 p-8 md:p-12 lg:p-16 text-white">
              <div className="text-center max-w-3xl mx-auto">
                <FadeIn direction="up">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                    {t('home.register.title', { communityName: currentCommunity?.name })}
                  </h2>
                </FadeIn>
                <FadeIn direction="up" delay={0.1}>
                  <p className="mb-8 text-white/90 text-lg">
                    {t('home.register.description', { communityName: currentCommunity?.name })}
                  </p>
                </FadeIn>
                <FadeIn direction="up" delay={0.2}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button 
                      className="bg-white text-primary hover:bg-white/90 transition-colors px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl"
                      onClick={() => {
                        if (!user) {
                          navigate('/auth?returnTo=/register-business');
                          return;
                        }
                        if (isBusinessOwner) {
                          navigate('/admin/my-business');
                        } else {
                          navigate('/register-business');
                        }
                      }}
                      disabled={isBusinessLoading}
                    >
                      {isBusinessOwner ? t('home.manage.business') : t('home.register.cta')}
                    </Button>
                  </motion.div>
                </FadeIn>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
